import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatCard, StatusBadge } from "@/components/common";
import { rupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Coins, RefreshCw, AlertTriangle, Store } from "lucide-react";

export default function SalesReceivables() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [marketplaces, setMarketplaces] = useState([]);
  const [filters, setFilters] = useState({ marketplace_id: "", status: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setRows(null);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    api.get(`/sales/receivables?${q.toString()}`).then((r) => setRows(r.data));
    api.get("/sales/receivables/summary").then((r) => setSummary(r.data));
  };
  useEffect(load, [filters]);
  useEffect(() => { api.get("/marketplaces").then((r) => setMarketplaces(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/sales/generate-missing"); toast.success(`${r.data.created} jurnal penjualan dibuat`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const completeHpp = async (id) => {
    try { await api.post(`/sales/receivables/${id}/complete-hpp`); toast.success("HPP dilengkapi, jurnal siap diposting"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const s = summary || {};
  const aging = s.aging || {};
  return (
    <div>
      <PageHeader title="Penjualan & Piutang" subtitle="Pendapatan diakui saat order SELESAI (jurnal DRAFT). Piutang per order, ringkasan per marketplace." testId="ar-sales-header">
        {can("ar.sales.manage") && <Button onClick={generate} disabled={busy} data-testid="generate-sales-btn" className="bg-indigo-600 hover:bg-indigo-500"><RefreshCw className="mr-1.5 h-4 w-4" />Generate Jurnal Penjualan</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 mb-6">
        <StatCard label="Total Piutang" value={rupiah(s.total_piutang)} icon={Coins} accent="indigo" testId="ar-total" />
        <StatCard label="0-7 Hari" value={rupiah(aging["0-7 Hari"])} icon={Coins} accent="slate" />
        <StatCard label="8-14 Hari" value={rupiah(aging["8-14 Hari"])} icon={Coins} accent="amber" />
        <StatCard label="15-30 Hari" value={rupiah(aging["15-30 Hari"])} icon={Coins} accent="sky" />
        <StatCard label="> 30 Hari" value={rupiah(aging["> 30 Hari"])} icon={AlertTriangle} accent="rose" />
        <StatCard label="HPP Belum Tersedia" value={s.hpp_pending_count || 0} icon={AlertTriangle} accent="amber" testId="ar-hpp-pending" hint="Revenue tetap bisa diposting; jurnal HPP menyusul" />
      </div>

      {s.by_marketplace && s.by_marketplace.length > 0 && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Store className="h-3.5 w-3.5" />Piutang per Marketplace</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {s.by_marketplace.map((m) => (
              <div key={m.marketplace_name} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`ar-mk-${m.marketplace_name}`}>
                <p className="text-xs text-slate-500">{m.marketplace_name}</p>
                <p className="font-display text-lg font-bold text-slate-900">{rupiah(m.outstanding)}</p>
                <p className="text-[11px] text-slate-400">{m.count} order</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={filters.marketplace_id || "all"} onValueChange={(v) => setFilters({ ...filters, marketplace_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-56" data-testid="ar-filter-marketplace"><SelectValue placeholder="Semua Marketplace" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Marketplace</SelectItem>{marketplaces.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="ar-filter-status"><SelectValue placeholder="Semua Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Outstanding">Outstanding</SelectItem><SelectItem value="Settled Partial">Settled Partial</SelectItem><SelectItem value="Settled">Settled</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada piutang penjualan" subtitle="Piutang muncul saat order berstatus SELESAI." icon={Coins} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No Order</TableHead><TableHead>Marketplace</TableHead><TableHead>Tanggal</TableHead>
              <TableHead className="text-right">Penjualan</TableHead><TableHead className="text-right">Fee</TableHead>
              <TableHead className="text-right">Piutang</TableHead><TableHead className="text-right">Sisa</TableHead>
              <TableHead>Umur</TableHead><TableHead>Jurnal</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`ar-row-${r.order_no}`}>
                  <TableCell className="font-mono text-xs">{r.order_no}</TableCell>
                  <TableCell>{r.marketplace_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(r.invoice_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(r.gross_sales)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{rupiah(r.marketplace_fee)}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(r.receivable_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-rose-600">{rupiah(r.outstanding)}</TableCell>
                  <TableCell><StatusBadge status={r.aging_bucket} /></TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs">
                      <span className="flex items-center gap-1"><span className="text-slate-400">Rev</span><StatusBadge status={r.journal_status} /></span>
                      {r.hpp_pending
                        ? <span className="font-semibold text-amber-600">HPP belum tersedia</span>
                        : r.cogs_journal_status && <span className="flex items-center gap-1"><span className="text-slate-400">HPP</span><StatusBadge status={r.cogs_journal_status} /></span>}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">
                    {r.hpp_pending && can("ar.sales.manage") && <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600" onClick={() => completeHpp(r.id)} data-testid={`complete-hpp-${r.order_no}`}>Lengkapi HPP</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
